/* eslint-disable react/prop-types */
import { useState } from 'react';
import { Tag, Loader2, X, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { validatePromoCode, removePromoCode } from '@/api/functions';
import { formatDiscountAmount } from '@/utils/discountCodes';
import { toast } from 'sonner';
import { AnimatePresence, motion } from 'framer-motion';
import SectionBand from '@/components/ui/SectionBand';
import { REVIEW_SECTION_CARD } from '@/constants/brandColors';

/**
 * Promo code field - SECURITY: Validation is server-side only.
 * No valid codes are stored or exposed in client code.
 *
 * One field, two possible effects, decided entirely by the server:
 *   - a DISCOUNT code (`discount` prop set) takes a flat sum off the trip total
 *   - a legacy promo code waives the service fee
 * The component never decides which; it renders whatever came back.
 */
export default function PromoCodeField({ cartId, applied, discount, onApplied, onRemoved, disabled }) {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const hasDiscount = !!discount && discount.cents > 0;
  const isApplied = applied || hasDiscount;
  const appliedLabel = hasDiscount
    ? `${formatDiscountAmount(discount.cents)} off your trip`
    : '0% service fee applied';

  const handleApply = async () => {
    const code = (input || '').trim();
    if (!code || !cartId) return;
    setLoading(true);
    try {
      const result = await validatePromoCode(code, cartId);
      if (result?.valid) {
        setInput('');
        onApplied?.(result);
        toast.success(
          result.kind === 'discount'
            ? `${result.discount_label} off applied!`
            : 'Promo applied!'
        );
        setIsExpanded(false);
      } else {
        toast.error(result?.error || 'Invalid promo code');
      }
    } catch (e) {
      toast.error('Could not validate promo');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async () => {
    if (!cartId) return;
    setLoading(true);
    try {
      const ok = await removePromoCode(cartId);
      if (ok) {
        onRemoved?.();
        toast.success('Promo removed');
      }
    } catch (e) {
      toast.error('Could not remove promo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={REVIEW_SECTION_CARD}>
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full text-left hover:bg-brand-900/20 transition-colors"
        aria-expanded={isExpanded}
      >
        <SectionBand
          icon={Tag}
          title="Promo Code"
          subtitle={isApplied ? appliedLabel : 'Have a code? Apply it to your trip'}
          rightSlot={
            <div className="flex items-center gap-2 flex-shrink-0">
              {isApplied && (
                <span className="text-[11px] font-normal text-emerald-300 bg-emerald-400/10 border border-emerald-400/15 px-2.5 py-0.5 rounded-full inline-flex items-center gap-1 whitespace-nowrap">
                  <Check className="w-3 h-3" />
                  Applied
                </span>
              )}
              {isExpanded ? (
                <ChevronUp className="w-4 h-4 text-white/45" />
              ) : (
                <ChevronDown className="w-4 h-4 text-white/45" />
              )}
            </div>
          }
        />
      </button>

      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            key="promo-content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-4 md:px-6 py-5">
              {isApplied ? (
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 flex-shrink-0 rounded-full bg-green-500/20 flex items-center justify-center">
                      <Check className="w-4 h-4 text-green-400" />
                    </div>
                    <span className="text-green-400 font-normal text-sm">{appliedLabel}</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleRemove}
                    disabled={disabled || loading}
                    className="text-green-400 hover:text-green-300 hover:bg-green-500/10 rounded-2xl"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <div className="relative flex-1 min-w-0">
                    <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 flex-shrink-0" />
                    <Input
                      type="text"
                      placeholder="Enter promo code"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleApply()}
                      disabled={disabled || loading}
                      className="pl-9 h-10 sm:h-9 bg-white/5 border-white/10 text-white placeholder:text-white/40 rounded-2xl focus-visible:ring-white/20"
                      autoComplete="off"
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="default"
                    onClick={handleApply}
                    disabled={disabled || loading || !input.trim()}
                    className="bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white rounded-2xl h-10 sm:h-9 px-4 sm:px-6 flex-shrink-0"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
